import { useState, useEffect, useRef } from 'react';
import { categories as PRESET_CATS } from '@data/categories';

const API = import.meta.env.VITE_API_URL || '/api';

const EMPTY = {
  name: '', description: '', price: '', mrp: '', category_id: '', stock: '',
  badge: '', hsn_code: '', barcode: '', weight: '', weight_unit: 'g'
};

function generateEAN13() {
  const prefix = '200';
  const mid = Math.floor(Math.random() * 1000000000).toString().padStart(9, '0');
  const digits = (prefix + mid).split('').map(Number);
  const sum = digits.reduce((acc, d, i) => acc + d * (i % 2 === 0 ? 1 : 3), 0);
  return prefix + mid + ((10 - (sum % 10)) % 10);
}

function emptyRow() {
  return { name: '', price: '', mrp: '', category_id: '', stock: '', badge: '', hsn_code: '', barcode: '', weight: '', weight_unit: 'g', variants: '' };
}
function emptyVariantGroup() { return { type: '', options: [{ label: '', price: '', stock: '' }] }; }
function emptyVariantOption() { return { label: '', price: '', stock: '' }; }

// ── Shared styles ─────────────────────────────────────────────────────────────
const S = {
  input: { width: '100%', padding: '.75rem .9rem', background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,184,0,.22)', borderRadius: 6, color: '#FFE082', fontFamily: "'Crimson Pro',serif", fontSize: '.9rem', outline: 'none', boxSizing: 'border-box' },
  smInput: { width: '100%', padding: '.55rem .7rem', background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,184,0,.18)', borderRadius: 5, color: '#FFE082', fontFamily: "'Crimson Pro',serif", fontSize: '.84rem', outline: 'none', boxSizing: 'border-box' },
  label: { display: 'block', fontFamily: "'Cinzel',serif", fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase', color: 'rgba(255,184,0,.6)', marginBottom: '.4rem' },
  select: { padding: '.75rem .9rem', background: '#1a0828', border: '1px solid rgba(255,184,0,.22)', borderRadius: 6, color: '#FFE082', fontFamily: "'Cinzel',serif", fontSize: '.8rem', outline: 'none', cursor: 'pointer' },
  opt: { background: '#1a0828', color: '#FFE082' },
  wrap: { marginBottom: '1rem' },
  panelBg: { background: 'linear-gradient(135deg,rgba(20,4,0,.98),rgba(26,10,46,.98))', border: '1px solid rgba(255,184,0,.25)', borderRadius: 10, padding: '2rem', marginBottom: '2rem' },
  sectionHdr: { fontFamily: "'Cinzel',serif", fontSize: '.78rem', letterSpacing: '.2em', textTransform: 'uppercase', color: '#FFB800', marginBottom: '1.5rem' },
};

export default function ProductsManager() {
  const [products, setProducts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [form, setForm]           = useState(EMPTY);
  const [editing, setEditing]     = useState(null);
  const [showForm, setShowForm]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState('');
  const [search, setSearch]       = useState('');

  // images
  const [imageUrls, setImageUrls]           = useState([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [imagePasteUrl, setImagePasteUrl]   = useState('');
  const [imageDragOver, setImageDragOver]   = useState(false);
  const imageInputRef = useRef();

  // variants
  const [variants, setVariants] = useState([]);   // [{ type, options:[{label,price,stock}] }]

  // custom fields  →  stored as specifications JSON object on save
  const [customFields, setCustomFields] = useState([]);  // [{ name:'', value:'' }]

  // category: 'select' uses dropdown, 'custom' shows text input
  const [catMode, setCatMode] = useState('select');

  // bulk
  const [showBulk, setShowBulk]     = useState(false);
  const [bulkTab, setBulkTab]       = useState('csv');
  const [csvData, setCsvData]       = useState([]);
  const [csvErrors, setCsvErrors]   = useState([]);
  const [quickRows, setQuickRows]   = useState([emptyRow()]);
  const [bulkMsg, setBulkMsg]       = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const csvInputRef = useRef();

  const token = localStorage.getItem('admin_token');

  const fetchProducts = () => {
    setLoading(true);
    fetch(`${API}/get-products.php`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setProducts(d.products || []))
      .catch(() => setProducts([])).finally(() => setLoading(false));
  };
  useEffect(() => { fetchProducts(); }, []);

  const resetForm = () => { setForm(EMPTY); setImageUrls([]); setImagePasteUrl(''); setVariants([]); setCustomFields([]); setEditing(null); setMsg(''); setCatMode('select'); };

  // ── Barcode helpers ───────────────────────────────────────────────────────
  const getUniqueBarcode = (excludeBarcode = null) => {
    const used = new Set(products.map(p => p.barcode).filter(b => b && b !== excludeBarcode));
    let bc; do { bc = generateEAN13(); } while (used.has(bc));
    return bc;
  };

  // ── Image upload ──────────────────────────────────────────────────────────
  const uploadImageFiles = async (files) => {
    if (!files?.length) return;
    setImageUploading(true);
    const newUrls = [];
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue;
      const fd = new FormData(); fd.append('image', file);
      const r = await fetch(`${API}/upload.php`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd })
        .then(r => r.json()).catch(() => ({ success: false }));
      if (r.success) newUrls.push(r.url);
    }
    setImageUrls(prev => [...prev, ...newUrls]);
    setImageUploading(false);
  };
  const addPasteUrl = () => { const u = imagePasteUrl.trim(); if (u) { setImageUrls(p => [...p, u]); setImagePasteUrl(''); } };

  // ── Variant helpers ───────────────────────────────────────────────────────
  const addGroup      = () => setVariants(v => [...v, emptyVariantGroup()]);
  const removeGroup   = (i) => setVariants(v => v.filter((_, j) => j !== i));
  const setGroupType  = (i, t) => setVariants(v => v.map((g, j) => j === i ? { ...g, type: t } : g));
  const addOption     = (i) => setVariants(v => v.map((g, j) => j === i ? { ...g, options: [...g.options, emptyVariantOption()] } : g));
  const removeOption  = (i, k) => setVariants(v => v.map((g, j) => j === i ? { ...g, options: g.options.filter((_, l) => l !== k) } : g));
  const setOptField   = (i, k, field, val) => setVariants(v => v.map((g, j) => j === i ? { ...g, options: g.options.map((o, l) => l === k ? { ...o, [field]: val } : o) } : g));

  // ── Form submit ───────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setMsg('');
    let barcode = form.barcode.trim();
    if (!barcode) {
      barcode = getUniqueBarcode(editing ? products.find(p => p.id === editing)?.barcode : null);
    } else {
      const dup = products.find(p => p.barcode === barcode && p.id !== editing);
      if (dup) { setMsg(`❌ Barcode "${barcode}" already used by "${dup.name}"`); setSaving(false); return; }
    }
    // Build specifications object from custom fields
    const specsObj = {};
    customFields.filter(f => f.name.trim()).forEach(f => { specsObj[f.name.trim()] = f.value; });

    const body = { ...form, barcode, images: imageUrls, variants, specifications: JSON.stringify(specsObj), id: editing };
    const r = await fetch(`${API}/${editing ? 'update-product.php' : 'create-product.php'}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body),
    }).then(r => r.json()).catch(() => ({ success: false }));
    setSaving(false);
    if (r.success) { setMsg(editing ? '✅ Updated!' : '✅ Created!'); resetForm(); setShowForm(false); fetchProducts(); }
    else setMsg(`❌ ${r.message || 'Error saving product'}`);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this product?')) return;
    await fetch(`${API}/delete-product.php`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ id }) });
    fetchProducts();
  };

  const handleEdit = (p) => {
    setEditing(p.id);
    setForm({ name: p.name||'', description: p.description||'', price: p.price||'', mrp: p.mrp||'', category_id: p.category_id||'', stock: p.stock||'', badge: p.badge||'', hsn_code: p.hsn_code||'', barcode: p.barcode||'', weight: p.weight||'', weight_unit: p.weight_unit||'g' });
    setImageUrls(p.images || []);
    setVariants(p.variants || []);
    // Load specifications into custom fields editor
    const specs = p.specifications || {};
    setCustomFields(
      Array.isArray(specs)
        ? specs
        : Object.entries(specs).map(([name, value]) => ({ name, value: String(value) }))
    );
    setShowForm(true); setShowBulk(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── CSV Bulk ──────────────────────────────────────────────────────────────
  const handleCSVUpload = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const lines = ev.target.result.split('\n').filter(l => l.trim());
      if (lines.length < 2) { setCsvErrors(['CSV is empty']); return; }
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/^"|"$/g, ''));
      const errors = [], data = [];
      for (let i = 1; i < lines.length; i++) {
        const vals = lines[i].match(/(".*?"|[^,]+|(?<=,)(?=,)|^(?=,)|(?<=,)$)/g) || [];
        const row = {};
        headers.forEach((h, idx) => { row[h] = (vals[idx]||'').trim().replace(/^"|"$/g,''); });
        if (!row.name) { errors.push(`Row ${i}: Name required`); continue; }
        if (!row.price || isNaN(Number(row.price))) { errors.push(`Row ${i}: Valid price required`); continue; }
        data.push(row);
      }
      setCsvErrors(errors); setCsvData(data);
    };
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const h = 'name,description,price,mrp,category_id,stock,badge,hsn_code,barcode,weight,weight_unit,images,variants';
    const s = '"Brass Ganesha","6 inch brass idol",999,1499,idols,50,Bestseller,44141900,,250,g,https://example.com/img.jpg,"[{""type"":""Size"",""options"":[{""label"":""Small"",""price"":""999"",""stock"":""50""},{""label"":""Large"",""price"":""1499"",""stock"":""30""}]}]"';
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(new Blob([h+'\n'+s],{type:'text/csv'})), download:'cosmic-hindu-products-template.csv' });
    a.click();
  };

  const handleBulkCSVImport = async () => {
    if (!csvData.length) return;
    setBulkSaving(true); setBulkMsg('');
    const used = new Set(products.map(p => p.barcode).filter(Boolean));
    let ok = 0, fail = 0;
    for (const row of csvData) {
      let bc = (row.barcode||'').trim();
      if (!bc || used.has(bc)) { do { bc = generateEAN13(); } while (used.has(bc)); }
      used.add(bc);
      let rowVariants = [];
      if (row.variants) { try { rowVariants = JSON.parse(row.variants); } catch {} }
      const r = await fetch(`${API}/create-product.php`, {
        method:'POST', headers:{'Content-Type':'application/json', Authorization:`Bearer ${token}`},
        body: JSON.stringify({ name:row.name, description:row.description||'', price:row.price, mrp:row.mrp||row.price, category_id:row.category_id||'', stock:row.stock||0, badge:row.badge||'', hsn_code:row.hsn_code||'', barcode:bc, weight:row.weight||'', weight_unit:row.weight_unit||'g', images:row.images||'', specifications:'{}', variants:rowVariants }),
      }).then(r=>r.json()).catch(()=>({success:false}));
      if (r.success) ok++; else fail++;
    }
    setBulkSaving(false);
    setBulkMsg(`✅ Imported ${ok} products${fail?` | ❌ ${fail} failed`:''}`);
    fetchProducts(); setCsvData([]); setCsvErrors([]);
    if (csvInputRef.current) csvInputRef.current.value = '';
  };

  const handleQuickAdd = async () => {
    const valid = quickRows.filter(r => r.name && r.price);
    if (!valid.length) { setBulkMsg('❌ Add at least one row with Name and Price'); return; }
    setBulkSaving(true); setBulkMsg('');
    const used = new Set(products.map(p => p.barcode).filter(Boolean));
    let ok = 0, fail = 0;
    for (const row of valid) {
      let bc = (row.barcode||'').trim();
      if (!bc || used.has(bc)) { do { bc = generateEAN13(); } while (used.has(bc)); }
      used.add(bc);
      let rowVariants = [];
      if (row.variants) { try { rowVariants = JSON.parse(row.variants); } catch {} }
      const r = await fetch(`${API}/create-product.php`, {
        method:'POST', headers:{'Content-Type':'application/json', Authorization:`Bearer ${token}`},
        body: JSON.stringify({ ...row, barcode:bc, mrp:row.mrp||row.price, variants:rowVariants }),
      }).then(r=>r.json()).catch(()=>({success:false}));
      if (r.success) ok++; else fail++;
    }
    setBulkSaving(false);
    setBulkMsg(`✅ Added ${ok} products${fail?` | ❌ ${fail} failed`:''}`);
    fetchProducts(); setQuickRows([emptyRow()]);
  };

  const filtered = products.filter(p => !search || p.name?.toLowerCase().includes(search.toLowerCase()));

  // ── Sub-components ────────────────────────────────────────────────────────
  const F = ({ label, name, type='text', placeholder='', rows=null }) => (
    <div style={S.wrap}>
      <label style={S.label}>{label}</label>
      {rows
        ? <textarea rows={rows} value={form[name]} onChange={e=>setForm({...form,[name]:e.target.value})} placeholder={placeholder} style={{...S.input,resize:'vertical'}} />
        : <input type={type} value={form[name]} onChange={e=>setForm({...form,[name]:e.target.value})} placeholder={placeholder} style={S.input} />
      }
    </div>
  );

  const MsgBar = ({ m }) => !m ? null : (
    <div style={{ padding:'.75rem 1rem', borderRadius:6, marginBottom:'1rem', background:m.startsWith('✅')?'rgba(76,175,80,.15)':'rgba(192,57,43,.15)', border:`1px solid ${m.startsWith('✅')?'rgba(76,175,80,.3)':'rgba(192,57,43,.3)'}`, fontFamily:"'Cinzel',serif", fontSize:'.75rem', color:m.startsWith('✅')?'#4CAF50':'#ff6b6b' }}>{m}</div>
  );

  // Drag-and-drop image uploader
  const ImageUploader = () => (
    <div style={S.wrap}>
      <label style={S.label}>Product Images</label>
      {imageUrls.length > 0 && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:'.6rem', marginBottom:'.9rem' }}>
          {imageUrls.map((url,i) => (
            <div key={i} style={{ position:'relative', width:82, height:82, flexShrink:0 }}>
              <img src={url} alt="" style={{ width:82, height:82, objectFit:'cover', borderRadius:6, border:'1px solid rgba(255,184,0,.3)', display:'block' }} />
              <span style={{ position:'absolute', bottom:3, left:4, background:'rgba(0,0,0,.65)', color:'#FFB800', fontFamily:"'Cinzel',serif", fontSize:'.55rem', padding:'1px 5px', borderRadius:3 }}>{i+1}</span>
              <button type="button" onClick={()=>setImageUrls(imageUrls.filter((_,j)=>j!==i))}
                style={{ position:'absolute', top:-7, right:-7, width:20, height:20, borderRadius:'50%', background:'#C0392B', border:'2px solid #1a0828', color:'#fff', fontSize:'.6rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1, padding:0 }}>✕</button>
            </div>
          ))}
        </div>
      )}
      <div
        onDragOver={e=>{e.preventDefault();setImageDragOver(true);}}
        onDragLeave={e=>{if(!e.currentTarget.contains(e.relatedTarget))setImageDragOver(false);}}
        onDrop={e=>{e.preventDefault();setImageDragOver(false);uploadImageFiles(e.dataTransfer.files);}}
        onClick={()=>imageInputRef.current?.click()}
        style={{ border:`2px dashed ${imageDragOver?'#FFB800':'rgba(255,184,0,.28)'}`, borderRadius:8, padding:'1.8rem 1rem', textAlign:'center', cursor:imageUploading?'default':'pointer', background:imageDragOver?'rgba(255,184,0,.07)':'rgba(255,255,255,.02)', transition:'border-color .2s,background .2s', marginBottom:'.75rem', userSelect:'none' }}>
        <input ref={imageInputRef} type="file" accept="image/*" multiple onChange={e=>uploadImageFiles(e.target.files)} style={{display:'none'}} />
        {imageUploading
          ? <div style={{color:'#FFB800',fontFamily:"'Cinzel',serif",fontSize:'.78rem',letterSpacing:'.1em'}}>🌀 Uploading images…</div>
          : <>
              <div style={{fontSize:'2.2rem',marginBottom:'.4rem',lineHeight:1}}>📸</div>
              <div style={{fontFamily:"'Cinzel',serif",fontSize:'.72rem',color:'#FFB800',letterSpacing:'.1em',marginBottom:'.3rem'}}>Drag & drop images here</div>
              <div style={{fontFamily:"'Crimson Pro',serif",fontSize:'.82rem',color:'rgba(255,224,130,.4)'}}>or click to browse · JPEG, PNG, WebP, GIF · max 5 MB each</div>
            </>
        }
      </div>
      <div style={{display:'flex',gap:'.5rem'}}>
        <input type="url" value={imagePasteUrl} onChange={e=>setImagePasteUrl(e.target.value)} onKeyDown={e=>e.key==='Enter'&&(e.preventDefault(),addPasteUrl())} placeholder="Or paste an image URL…" style={{...S.input,flex:1}} />
        <button type="button" onClick={addPasteUrl} style={{ padding:'.75rem 1rem', background:'rgba(255,184,0,.12)', border:'1px solid rgba(255,184,0,.3)', borderRadius:6, color:'#FFB800', fontFamily:"'Cinzel',serif", fontSize:'.65rem', fontWeight:700, letterSpacing:'.1em', cursor:'pointer', whiteSpace:'nowrap' }}>+ Add</button>
      </div>
    </div>
  );

  // Variants editor
  const VariantsEditor = () => (
    <div style={{ ...S.wrap, marginBottom:'1.5rem' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'.6rem' }}>
        <label style={S.label}>Product Variants <span style={{fontFamily:"'Crimson Pro',serif",fontSize:'.75rem',color:'rgba(255,224,130,.4)',fontStyle:'italic',textTransform:'none',letterSpacing:0}}>(sizes, weights, colours…)</span></label>
        <button type="button" onClick={addGroup}
          style={{ padding:'.4rem 1rem', background:'rgba(255,184,0,.12)', border:'1px solid rgba(255,184,0,.3)', borderRadius:6, color:'#FFB800', fontFamily:"'Cinzel',serif", fontSize:'.62rem', fontWeight:700, letterSpacing:'.1em', cursor:'pointer', whiteSpace:'nowrap' }}>
          + Add Variant Group
        </button>
      </div>

      {variants.length === 0 && (
        <div style={{ padding:'.8rem 1rem', border:'1px dashed rgba(255,184,0,.15)', borderRadius:8, fontFamily:"'Crimson Pro',serif", fontSize:'.85rem', color:'rgba(255,224,130,.3)', fontStyle:'italic' }}>
          No variants yet. Click "+ Add Variant Group" to add sizes, weights, colours, etc.
        </div>
      )}

      {variants.map((group, i) => (
        <div key={i} style={{ background:'rgba(255,184,0,.04)', border:'1px solid rgba(255,184,0,.15)', borderRadius:8, padding:'1rem 1rem .75rem', marginBottom:'.75rem' }}>
          {/* Group header row */}
          <div style={{ display:'flex', gap:'.5rem', alignItems:'center', marginBottom:'.8rem' }}>
            <input value={group.type} onChange={e=>setGroupType(i,e.target.value)} placeholder="Variant type  e.g. Size / Weight / Colour / Pack"
              style={{ ...S.smInput, flex:1, fontFamily:"'Cinzel',serif", fontSize:'.75rem', letterSpacing:'.06em' }} />
            <button type="button" onClick={()=>removeGroup(i)}
              style={{ padding:'.5rem .9rem', background:'rgba(192,57,43,.12)', border:'1px solid rgba(192,57,43,.25)', borderRadius:6, color:'#ff6b6b', cursor:'pointer', fontFamily:"'Cinzel',serif", fontSize:'.62rem', letterSpacing:'.08em', whiteSpace:'nowrap' }}>
              ✕ Remove
            </button>
          </div>

          {/* Column headers */}
          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 28px', gap:'.4rem', padding:'0 .1rem', marginBottom:'.35rem' }}>
            {['Option label', 'Price (₹)', 'Stock', ''].map(h=>(
              <div key={h} style={{ fontFamily:"'Cinzel',serif", fontSize:'.55rem', letterSpacing:'.12em', color:'rgba(255,184,0,.45)', textTransform:'uppercase' }}>{h}</div>
            ))}
          </div>

          {/* Option rows */}
          {group.options.map((opt, k) => (
            <div key={k} style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 28px', gap:'.4rem', marginBottom:'.35rem' }}>
              <input value={opt.label} onChange={e=>setOptField(i,k,'label',e.target.value)} placeholder="e.g. 250g / Small / Red" style={S.smInput} />
              <input type="number" value={opt.price} onChange={e=>setOptField(i,k,'price',e.target.value)} placeholder="999" style={S.smInput} />
              <input type="number" value={opt.stock} onChange={e=>setOptField(i,k,'stock',e.target.value)} placeholder="50" style={S.smInput} />
              <button type="button" onClick={()=>removeOption(i,k)}
                style={{ padding:'.4rem 0', background:'rgba(192,57,43,.1)', border:'1px solid rgba(192,57,43,.2)', borderRadius:4, color:'#ff6b6b', cursor:'pointer', fontSize:'.72rem', width:28, textAlign:'center' }}>✕</button>
            </div>
          ))}

          <button type="button" onClick={()=>addOption(i)}
            style={{ marginTop:'.3rem', padding:'.35rem .8rem', background:'transparent', border:'1px dashed rgba(255,184,0,.22)', borderRadius:5, color:'rgba(255,184,0,.55)', fontFamily:"'Cinzel',serif", fontSize:'.6rem', letterSpacing:'.1em', cursor:'pointer' }}>
            + Add Option
          </button>
        </div>
      ))}
    </div>
  );

  // ── Custom Fields Editor ─────────────────────────────────────────────────
  const CustomFieldsEditor = () => (
    <div style={{ ...S.wrap, marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.6rem' }}>
        <label style={S.label}>
          Custom Product Details
          <span style={{ fontFamily: "'Crimson Pro',serif", fontSize: '.75rem', color: 'rgba(255,224,130,.4)', fontStyle: 'italic', textTransform: 'none', letterSpacing: 0 }}>
            {' '}— shown to customers on product page
          </span>
        </label>
        <button type="button"
          onClick={() => setCustomFields(cf => [...cf, { name: '', value: '' }])}
          style={{ padding: '.4rem 1rem', background: 'rgba(255,184,0,.12)', border: '1px solid rgba(255,184,0,.3)', borderRadius: 6, color: '#FFB800', fontFamily: "'Cinzel',serif", fontSize: '.62rem', fontWeight: 700, letterSpacing: '.1em', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          + Add Field
        </button>
      </div>

      {customFields.length === 0 && (
        <div style={{ padding: '.8rem 1rem', border: '1px dashed rgba(255,184,0,.15)', borderRadius: 8, fontFamily: "'Crimson Pro',serif", fontSize: '.85rem', color: 'rgba(255,224,130,.3)', fontStyle: 'italic' }}>
          No custom fields yet. Click "+ Add Field" to add details like Material, Origin, Dimensions, Care Instructions…
        </div>
      )}

      {customFields.length > 0 && (
        <>
          {/* Column headers */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr 28px', gap: '.5rem', padding: '0 .1rem', marginBottom: '.35rem' }}>
            {['Field Name', 'Value / Description', ''].map(h => (
              <div key={h} style={{ fontFamily: "'Cinzel',serif", fontSize: '.55rem', letterSpacing: '.12em', color: 'rgba(255,184,0,.45)', textTransform: 'uppercase' }}>{h}</div>
            ))}
          </div>

          {customFields.map((field, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr 28px', gap: '.5rem', marginBottom: '.4rem' }}>
              <input
                value={field.name}
                onChange={e => setCustomFields(cf => cf.map((f, j) => j === i ? { ...f, name: e.target.value } : f))}
                placeholder="e.g. Material"
                style={{ ...S.smInput, fontFamily: "'Cinzel',serif", fontSize: '.75rem', letterSpacing: '.05em' }}
              />
              <input
                value={field.value}
                onChange={e => setCustomFields(cf => cf.map((f, j) => j === i ? { ...f, value: e.target.value } : f))}
                placeholder="e.g. Pure Brass"
                style={S.smInput}
              />
              <button type="button"
                onClick={() => setCustomFields(cf => cf.filter((_, j) => j !== i))}
                style={{ padding: '.4rem 0', background: 'rgba(192,57,43,.1)', border: '1px solid rgba(192,57,43,.2)', borderRadius: 4, color: '#ff6b6b', cursor: 'pointer', fontSize: '.72rem', width: 28, textAlign: 'center' }}>✕</button>
            </div>
          ))}

          {/* Quick-add common fields */}
          <div style={{ marginTop: '.6rem', display: 'flex', flexWrap: 'wrap', gap: '.4rem', alignItems: 'center' }}>
            <span style={{ fontFamily: "'Cinzel',serif", fontSize: '.55rem', color: 'rgba(255,184,0,.4)', letterSpacing: '.1em', textTransform: 'uppercase', marginRight: '.2rem' }}>Quick add:</span>
            {['Material', 'Dimensions', 'Origin', 'Weight', 'Colour', 'Care Instructions', 'Suitable For', 'Packaging'].map(preset => (
              <button key={preset} type="button"
                onClick={() => {
                  if (customFields.some(f => f.name === preset)) return;
                  setCustomFields(cf => [...cf, { name: preset, value: '' }]);
                }}
                style={{ padding: '3px 10px', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,184,0,.15)', borderRadius: 20, color: customFields.some(f => f.name === preset) ? 'rgba(255,184,0,.3)' : 'rgba(255,224,130,.55)', fontFamily: "'Cinzel',serif", fontSize: '.58rem', letterSpacing: '.08em', cursor: customFields.some(f => f.name === preset) ? 'default' : 'pointer', transition: 'all .15s' }}>
                {preset}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.8rem', flexWrap:'wrap', gap:'1rem' }}>
        <h1 style={{ fontFamily:"'Cinzel Decorative',serif", fontSize:'1.5rem', background:'linear-gradient(135deg,#FFE082,#FF6B00)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
          🛍 Products Manager
        </h1>
        <div style={{ display:'flex', gap:'.75rem', flexWrap:'wrap' }}>
          <button onClick={()=>{setShowBulk(!showBulk);setShowForm(false);setBulkMsg('');}}
            style={{ padding:'.7rem 1.5rem', background:showBulk?'rgba(255,184,0,.2)':'rgba(255,184,0,.08)', border:'1px solid rgba(255,184,0,.3)', borderRadius:6, color:'#FFB800', fontFamily:"'Cinzel',serif", fontSize:'.72rem', fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', cursor:'pointer' }}>
            {showBulk ? '✕ Close Bulk' : '📦 Bulk Add'}
          </button>
          <button onClick={()=>{resetForm();setShowForm(!showForm);setShowBulk(false);}}
            style={{ padding:'.7rem 1.5rem', background:showForm?'rgba(255,107,0,.2)':'linear-gradient(135deg,#FF6B00,#FFB800)', border:showForm?'1px solid rgba(255,107,0,.4)':'none', borderRadius:6, color:showForm?'#FF6B00':'#0D0500', fontFamily:"'Cinzel',serif", fontSize:'.72rem', fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', cursor:'pointer' }}>
            {showForm ? '✕ Cancel' : '➕ Add Product'}
          </button>
        </div>
      </div>

      {/* ════ Single Product Form ════ */}
      {showForm && (
        <div style={S.panelBg}>
          <h3 style={S.sectionHdr}>{editing ? '✏️ Edit Product' : '➕ New Product'}</h3>
          {MsgBar({ m: msg })}
          <form onSubmit={handleSubmit}>

            {/* Basic fields grid */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:'0 1.5rem' }}>
              {F({ label:'Product Name *', name:'name', placeholder:'e.g. Brass Ganesha Idol' })}
              {/* ── Category Selector ── */}
              {(() => {
                // Build merged list: preset + any custom cats already in products
                const presetIds = new Set(PRESET_CATS.map(c => c.id));
                const customCats = [...new Set(products.map(p => p.category_id).filter(id => id && !presetIds.has(id)))];
                const allCats = [...PRESET_CATS, ...customCats.map(id => ({ id, name: id, icon: '📦' }))];

                return (
                  <div style={S.wrap}>
                    <label style={S.label}>Collection / Category *</label>
                    {catMode === 'select' ? (
                      <div style={{ display: 'flex', gap: '.5rem' }}>
                        <select
                          value={allCats.some(c => c.id === form.category_id) ? form.category_id : (form.category_id ? '__custom__' : '')}
                          onChange={e => {
                            if (e.target.value === '__add_new__') { setCatMode('custom'); setForm({ ...form, category_id: '' }); }
                            else setForm({ ...form, category_id: e.target.value });
                          }}
                          style={{ ...S.select, flex: 1, width: '100%' }}
                        >
                          <option value="" style={S.opt}>— Select Collection —</option>
                          {allCats.map(c => (
                            <option key={c.id} value={c.id} style={S.opt}>{c.icon || '📦'} {c.name}</option>
                          ))}
                          <option value="__add_new__" style={{ background: '#1a0828', color: '#FFB800' }}>➕ Add New Collection…</option>
                        </select>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '.5rem' }}>
                        <input
                          autoFocus
                          value={form.category_id}
                          onChange={e => setForm({ ...form, category_id: e.target.value })}
                          placeholder="e.g. crystals, copper-items, gifts…"
                          style={{ ...S.input, flex: 1 }}
                        />
                        <button type="button" onClick={() => setCatMode('select')}
                          style={{ padding: '.75rem 1rem', background: 'rgba(255,107,0,.12)', border: '1px solid rgba(255,107,0,.3)', borderRadius: 6, color: '#FF6B00', fontFamily: "'Cinzel',serif", fontSize: '.65rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          ← Back
                        </button>
                      </div>
                    )}
                    {form.category_id && (
                      <div style={{ marginTop: '.35rem', fontFamily: "'Cinzel',serif", fontSize: '.62rem', color: 'rgba(255,184,0,.55)', letterSpacing: '.1em' }}>
                        Selected: <span style={{ color: '#FFB800' }}>{form.category_id}</span>
                      </div>
                    )}
                  </div>
                );
              })()}
              {F({ label:'Price (₹) *', name:'price', type:'number', placeholder:'999' })}
              {F({ label:'MRP (₹)', name:'mrp', type:'number', placeholder:'1499' })}
              {F({ label:'Stock Quantity', name:'stock', type:'number', placeholder:'50' })}
              {F({ label:'Badge', name:'badge', placeholder:'Bestseller, New, Sacred…' })}
              {F({ label:'HSN Code', name:'hsn_code', placeholder:'e.g. 44141900' })}

              {/* Barcode */}
              <div style={S.wrap}>
                <label style={S.label}>Barcode (EAN-13) — leave blank to auto-assign</label>
                <div style={{display:'flex',gap:'.5rem'}}>
                  <input type="text" value={form.barcode} onChange={e=>setForm({...form,barcode:e.target.value})} placeholder="Leave blank to auto-assign" style={{...S.input,flex:1}} />
                  <button type="button" onClick={()=>setForm({...form,barcode:getUniqueBarcode(form.barcode||null)})}
                    style={{ padding:'.75rem 1rem', background:'rgba(255,184,0,.15)', border:'1px solid rgba(255,184,0,.3)', borderRadius:6, color:'#FFB800', fontFamily:"'Cinzel',serif", fontSize:'.65rem', fontWeight:700, letterSpacing:'.1em', cursor:'pointer', whiteSpace:'nowrap' }}>
                    ⚙ Auto
                  </button>
                </div>
              </div>

              {/* Weight + unit */}
              <div style={S.wrap}>
                <label style={S.label}>Weight (for shipping)</label>
                <div style={{display:'flex',gap:'.5rem'}}>
                  <input type="number" value={form.weight} onChange={e=>setForm({...form,weight:e.target.value})} placeholder="e.g. 250" style={{...S.input,flex:1}} />
                  <select value={form.weight_unit} onChange={e=>setForm({...form,weight_unit:e.target.value})} style={S.select}>
                    <option value="g" style={S.opt}>Grams (g)</option>
                    <option value="kg" style={S.opt}>Kilograms (kg)</option>
                  </select>
                </div>
              </div>
            </div>

            {F({ label:'Description', name:'description', rows:3, placeholder:'Product description…' })}

            {/* Image uploader */}
            {ImageUploader()}

            {/* ── Variants Editor ── */}
            <div style={{ borderTop:'1px solid rgba(255,184,0,.1)', paddingTop:'1.5rem', marginTop:'.5rem', marginBottom:'1rem' }}>
              {VariantsEditor()}
            </div>

            {/* ── Custom Fields Editor ── */}
            <div style={{ borderTop:'1px solid rgba(255,184,0,.1)', paddingTop:'1.5rem', marginTop:'.5rem' }}>
              {CustomFieldsEditor()}
            </div>

            <button type="submit" disabled={saving}
              style={{ padding:'.9rem 2rem', background:'linear-gradient(135deg,#FF6B00,#FFB800)', border:'none', borderRadius:6, color:'#0D0500', fontFamily:"'Cinzel',serif", fontSize:'.78rem', fontWeight:700, letterSpacing:'.15em', textTransform:'uppercase', cursor:saving?'not-allowed':'pointer', opacity:saving?.7:1 }}>
              {saving ? '🌀 Saving…' : `🙏 ${editing?'Update':'Create'} Product`}
            </button>
          </form>
        </div>
      )}

      {/* ════ Bulk Add Panel ════ */}
      {showBulk && (
        <div style={S.panelBg}>
          <h3 style={S.sectionHdr}>📦 Bulk Add Products</h3>

          {/* Tabs */}
          <div style={{ display:'flex', gap:'.5rem', marginBottom:'1.5rem', borderBottom:'1px solid rgba(255,184,0,.15)' }}>
            {[['csv','📄 CSV Import'],['quick','⚡ Quick Add Table']].map(([t,label])=>(
              <button key={t} onClick={()=>{setBulkTab(t);setBulkMsg('');}}
                style={{ padding:'.6rem 1.2rem', background:bulkTab===t?'rgba(255,184,0,.15)':'transparent', border:`1px solid ${bulkTab===t?'rgba(255,184,0,.3)':'transparent'}`, borderBottom:bulkTab===t?'2px solid #FFB800':'2px solid transparent', borderRadius:'6px 6px 0 0', color:bulkTab===t?'#FFB800':'rgba(255,224,130,.4)', fontFamily:"'Cinzel',serif", fontSize:'.65rem', letterSpacing:'.12em', textTransform:'uppercase', cursor:'pointer' }}>
                {label}
              </button>
            ))}
          </div>

          {MsgBar({ m: bulkMsg })}

          {/* CSV Tab */}
          {bulkTab === 'csv' && (
            <div>
              <div style={{ display:'flex', gap:'1rem', marginBottom:'1.2rem', flexWrap:'wrap', alignItems:'center' }}>
                <button onClick={downloadTemplate} style={{ padding:'.6rem 1.2rem', background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,184,0,.22)', borderRadius:6, color:'#FFE082', fontFamily:"'Cinzel',serif", fontSize:'.65rem', letterSpacing:'.1em', cursor:'pointer' }}>⬇ Download CSV Template</button>
                <label style={{ padding:'.6rem 1.2rem', background:'rgba(255,184,0,.12)', border:'1px solid rgba(255,184,0,.3)', borderRadius:6, color:'#FFB800', fontFamily:"'Cinzel',serif", fontSize:'.65rem', letterSpacing:'.1em', cursor:'pointer' }}>
                  📂 Choose CSV File
                  <input ref={csvInputRef} type="file" accept=".csv" onChange={handleCSVUpload} style={{display:'none'}} />
                </label>
                <span style={{ fontSize:'.75rem', color:'rgba(255,224,130,.35)', fontFamily:"'Crimson Pro',serif" }}>
                  Columns: name, price, mrp, category_id, stock, badge, hsn_code, barcode, weight, weight_unit, images, variants (JSON)
                </span>
              </div>

              {csvErrors.length > 0 && (
                <div style={{ background:'rgba(192,57,43,.1)', border:'1px solid rgba(192,57,43,.3)', borderRadius:6, padding:'1rem', marginBottom:'1rem' }}>
                  <div style={{ fontFamily:"'Cinzel',serif", fontSize:'.65rem', color:'#ff6b6b', marginBottom:'.5rem', letterSpacing:'.1em' }}>⚠ VALIDATION ERRORS</div>
                  {csvErrors.map((e,i)=><div key={i} style={{ fontSize:'.82rem', color:'rgba(255,107,107,.8)', fontFamily:"'Crimson Pro',serif" }}>{e}</div>)}
                </div>
              )}

              {csvData.length > 0 && (
                <div>
                  <div style={{ fontFamily:"'Cinzel',serif", fontSize:'.65rem', color:'rgba(255,224,130,.6)', marginBottom:'.75rem', letterSpacing:'.1em' }}>PREVIEW — {csvData.length} PRODUCTS READY TO IMPORT</div>
                  <div style={{ overflowX:'auto', marginBottom:'1.2rem' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'.78rem', fontFamily:"'Crimson Pro',serif" }}>
                      <thead>
                        <tr style={{ borderBottom:'1px solid rgba(255,184,0,.2)' }}>
                          {['Name','Price','Category','Stock','HSN','Barcode','Weight','Variants'].map(h=>(
                            <th key={h} style={{ padding:'.5rem .75rem', textAlign:'left', fontFamily:"'Cinzel',serif", fontSize:'.6rem', letterSpacing:'.12em', color:'rgba(255,184,0,.6)', whiteSpace:'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvData.slice(0,5).map((row,i)=>{
                          let vCount = 0;
                          if (row.variants) { try { const v=JSON.parse(row.variants); vCount=v.reduce((a,g)=>a+(g.options?.length||0),0); } catch {} }
                          return (
                            <tr key={i} style={{ borderBottom:'1px solid rgba(255,255,255,.05)' }}>
                              <td style={{ padding:'.4rem .75rem', color:'#FFE082' }}>{row.name}</td>
                              <td style={{ padding:'.4rem .75rem', color:'#FFB800' }}>₹{row.price}</td>
                              <td style={{ padding:'.4rem .75rem', color:'rgba(255,224,130,.6)' }}>{row.category_id||'—'}</td>
                              <td style={{ padding:'.4rem .75rem', color:'rgba(255,224,130,.6)' }}>{row.stock||0}</td>
                              <td style={{ padding:'.4rem .75rem', color:'rgba(255,224,130,.6)' }}>{row.hsn_code||'—'}</td>
                              <td style={{ padding:'.4rem .75rem', color:'rgba(255,224,130,.6)' }}>{row.barcode||'(auto)'}</td>
                              <td style={{ padding:'.4rem .75rem', color:'rgba(255,224,130,.6)' }}>{row.weight?`${row.weight} ${row.weight_unit||'g'}`:'—'}</td>
                              <td style={{ padding:'.4rem .75rem', color:'rgba(255,224,130,.6)' }}>{vCount?`${vCount} options`:'—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {csvData.length > 5 && <div style={{ fontSize:'.75rem', color:'rgba(255,224,130,.35)', padding:'.5rem .75rem', fontFamily:"'Cinzel',serif" }}>…and {csvData.length-5} more rows</div>}
                  </div>
                  <button onClick={handleBulkCSVImport} disabled={bulkSaving}
                    style={{ padding:'.9rem 2rem', background:'linear-gradient(135deg,#FF6B00,#FFB800)', border:'none', borderRadius:6, color:'#0D0500', fontFamily:"'Cinzel',serif", fontSize:'.78rem', fontWeight:700, letterSpacing:'.15em', textTransform:'uppercase', cursor:bulkSaving?'not-allowed':'pointer' }}>
                    {bulkSaving?'🌀 Importing…':`🙏 Import ${csvData.length} Products`}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Quick Add Table Tab */}
          {bulkTab === 'quick' && (
            <div>
              <div style={{ overflowX:'auto', marginBottom:'1rem' }}>
                <table style={{ borderCollapse:'collapse', fontSize:'.78rem', fontFamily:"'Crimson Pro',serif", minWidth:1040 }}>
                  <thead>
                    <tr style={{ borderBottom:'1px solid rgba(255,184,0,.2)' }}>
                      {['Name *','Price * (₹)','MRP (₹)','Category','Stock','Badge','HSN','Barcode','Weight','Unit','Variants (JSON)',''].map(h=>(
                        <th key={h} style={{ padding:'.5rem .6rem', textAlign:'left', fontFamily:"'Cinzel',serif", fontSize:'.55rem', letterSpacing:'.09em', color:'rgba(255,184,0,.6)', whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {quickRows.map((row,i)=>(
                      <tr key={i} style={{ borderBottom:'1px solid rgba(255,255,255,.04)' }}>
                        {[{field:'name',w:140},{field:'price',w:75},{field:'mrp',w:75},{field:'category_id',w:95},{field:'stock',w:60},{field:'badge',w:95},{field:'hsn_code',w:85},{field:'barcode',w:110}].map(({field,w})=>(
                          <td key={field} style={{padding:'.3rem .4rem'}}>
                            <input value={row[field]} onChange={e=>{const r=[...quickRows];r[i]={...r[i],[field]:e.target.value};setQuickRows(r);}} placeholder={field==='barcode'?'(auto)':''} style={{ width:w, padding:'.45rem .6rem', background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,184,0,.18)', borderRadius:4, color:'#FFE082', fontFamily:"'Crimson Pro',serif", fontSize:'.82rem', outline:'none', boxSizing:'border-box' }} />
                          </td>
                        ))}
                        <td style={{padding:'.3rem .4rem'}}>
                          <input type="number" value={row.weight} onChange={e=>{const r=[...quickRows];r[i]={...r[i],weight:e.target.value};setQuickRows(r);}} placeholder="0" style={{ width:65, padding:'.45rem .6rem', background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,184,0,.18)', borderRadius:4, color:'#FFE082', fontFamily:"'Crimson Pro',serif", fontSize:'.82rem', outline:'none', boxSizing:'border-box' }} />
                        </td>
                        <td style={{padding:'.3rem .4rem'}}>
                          <select value={row.weight_unit} onChange={e=>{const r=[...quickRows];r[i]={...r[i],weight_unit:e.target.value};setQuickRows(r);}} style={{...S.select,padding:'.45rem .5rem',fontSize:'.75rem'}}>
                            <option value="g" style={S.opt}>g</option>
                            <option value="kg" style={S.opt}>kg</option>
                          </select>
                        </td>
                        <td style={{padding:'.3rem .4rem'}}>
                          <input value={row.variants||''} onChange={e=>{const r=[...quickRows];r[i]={...r[i],variants:e.target.value};setQuickRows(r);}} placeholder='[{"type":"Size","options":[{"label":"S","price":"499","stock":"20"}]}]' style={{ width:200, padding:'.45rem .6rem', background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,184,0,.18)', borderRadius:4, color:'#FFE082', fontFamily:"'Crimson Pro',serif", fontSize:'.75rem', outline:'none', boxSizing:'border-box' }} />
                        </td>
                        <td style={{padding:'.3rem .4rem'}}>
                          <button onClick={()=>setQuickRows(quickRows.filter((_,j)=>j!==i))} style={{ padding:'.4rem .7rem', background:'rgba(192,57,43,.12)', border:'1px solid rgba(192,57,43,.25)', borderRadius:4, color:'#ff6b6b', cursor:'pointer', fontSize:'.8rem' }}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize:'.75rem', color:'rgba(255,224,130,.35)', fontFamily:"'Crimson Pro',serif", marginBottom:'1rem', fontStyle:'italic' }}>
                Tip: Leave the Variants column blank — you can add variants by editing the product after import.
              </div>
              <div style={{ display:'flex', gap:'.75rem', flexWrap:'wrap', alignItems:'center' }}>
                <button onClick={()=>setQuickRows([...quickRows,emptyRow()])} style={{ padding:'.6rem 1.2rem', background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,184,0,.22)', borderRadius:6, color:'#FFE082', fontFamily:"'Cinzel',serif", fontSize:'.65rem', letterSpacing:'.1em', cursor:'pointer' }}>+ Add Row</button>
                <button onClick={()=>setQuickRows([...quickRows,...Array(5).fill(null).map(()=>emptyRow())])} style={{ padding:'.6rem 1.2rem', background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,184,0,.22)', borderRadius:6, color:'#FFE082', fontFamily:"'Cinzel',serif", fontSize:'.65rem', letterSpacing:'.1em', cursor:'pointer' }}>+ Add 5 Rows</button>
                <button onClick={handleQuickAdd} disabled={bulkSaving} style={{ padding:'.9rem 2rem', background:'linear-gradient(135deg,#FF6B00,#FFB800)', border:'none', borderRadius:6, color:'#0D0500', fontFamily:"'Cinzel',serif", fontSize:'.78rem', fontWeight:700, letterSpacing:'.15em', textTransform:'uppercase', cursor:bulkSaving?'not-allowed':'pointer' }}>
                  {bulkSaving?'🌀 Saving…':'🙏 Add All Products'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Search */}
      <div style={{ marginBottom:'1.2rem' }}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search products…"
          style={{ width:'100%', maxWidth:380, padding:'.7rem 1rem', background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,184,0,.22)', borderRadius:6, color:'#FFE082', fontFamily:"'Crimson Pro',serif", fontSize:'.9rem', outline:'none', boxSizing:'border-box' }} />
      </div>

      {/* Products Grid */}
      {loading ? (
        <div style={{ textAlign:'center', padding:'3rem', color:'rgba(255,224,130,.4)', fontFamily:"'Cinzel',serif" }}>🌀 Loading products…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'3rem', color:'rgba(255,224,130,.3)', fontFamily:"'Cinzel',serif", fontSize:'.85rem', letterSpacing:'.1em' }}>No products found</div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:'1.2rem' }}>
          {filtered.map(p=>(
            <div key={p.id} style={{ background:'linear-gradient(135deg,rgba(20,4,0,.95),rgba(26,10,46,.95))', border:'1px solid rgba(255,184,0,.18)', borderRadius:8, overflow:'hidden' }}>
              {p.images?.[0]
                ? <img src={p.images[0]} alt={p.name} style={{ width:'100%', height:160, objectFit:'cover' }} />
                : <div style={{ width:'100%', height:100, background:'rgba(255,184,0,.06)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'2.5rem' }}>🪔</div>
              }
              <div style={{ padding:'1rem' }}>
                <div style={{ fontFamily:"'Cinzel',serif", fontSize:'.8rem', fontWeight:600, color:'#FFE082', marginBottom:'.3rem', lineHeight:1.35 }}>{p.name}</div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'.4rem' }}>
                  <span style={{ fontFamily:"'Cinzel',serif", fontSize:'.9rem', color:'#FFB800', fontWeight:700 }}>₹{Number(p.price).toLocaleString('en-IN')}</span>
                  <span style={{ fontSize:'.72rem', color:p.stock>10?'#4CAF50':p.stock>0?'#FFB800':'#C0392B', fontFamily:"'Cinzel',serif" }}>Stock: {p.stock}</span>
                </div>

                {/* Variants chips */}
                {p.variants?.length > 0 && (
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'.3rem', marginBottom:'.5rem' }}>
                    {p.variants.map((v,i)=>(
                      <span key={i} style={{ fontSize:'.62rem', background:'rgba(255,184,0,.1)', border:'1px solid rgba(255,184,0,.22)', borderRadius:4, padding:'2px 8px', color:'#FFB800', fontFamily:"'Cinzel',serif" }}>
                        {v.type || 'Variant'}: {v.options?.length || 0}
                      </span>
                    ))}
                  </div>
                )}

                {/* Variant options preview (first group) */}
                {p.variants?.[0]?.options?.length > 0 && (
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'.25rem', marginBottom:'.5rem' }}>
                    {p.variants[0].options.slice(0,4).map((o,i)=>(
                      <span key={i} style={{ fontSize:'.6rem', background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,184,0,.1)', borderRadius:3, padding:'2px 6px', color:'rgba(255,224,130,.65)', fontFamily:"'Crimson Pro',serif" }}>
                        {o.label}{o.price?` ₹${o.price}`:''}
                      </span>
                    ))}
                    {p.variants[0].options.length > 4 && (
                      <span style={{ fontSize:'.6rem', color:'rgba(255,224,130,.35)', fontFamily:"'Crimson Pro',serif", padding:'2px 0' }}>+{p.variants[0].options.length-4} more</span>
                    )}
                  </div>
                )}

                {/* Meta: barcode / weight / HSN */}
                <div style={{ display:'flex', flexWrap:'wrap', gap:'.4rem', marginBottom:'.7rem' }}>
                  {p.barcode   && <span style={{ fontSize:'.6rem', color:'rgba(255,224,130,.4)', fontFamily:"'Cinzel',serif" }}>#{p.barcode}</span>}
                  {p.weight    && <span style={{ fontSize:'.6rem', color:'rgba(255,224,130,.4)', fontFamily:"'Cinzel',serif" }}>⚖ {p.weight}{p.weight_unit||'g'}</span>}
                  {p.hsn_code  && <span style={{ fontSize:'.6rem', color:'rgba(255,224,130,.4)', fontFamily:"'Cinzel',serif" }}>HSN {p.hsn_code}</span>}
                </div>

                <div style={{ display:'flex', gap:'.5rem' }}>
                  <button onClick={()=>handleEdit(p)} style={{ flex:1, padding:'.5rem', background:'rgba(255,184,0,.12)', border:'1px solid rgba(255,184,0,.25)', borderRadius:4, color:'#FFE082', cursor:'pointer', fontFamily:"'Cinzel',serif", fontSize:'.65rem', letterSpacing:'.1em' }}>✏️ Edit</button>
                  <button onClick={()=>handleDelete(p.id)} style={{ flex:1, padding:'.5rem', background:'rgba(192,57,43,.12)', border:'1px solid rgba(192,57,43,.25)', borderRadius:4, color:'#ff6b6b', cursor:'pointer', fontFamily:"'Cinzel',serif", fontSize:'.65rem', letterSpacing:'.1em' }}>🗑 Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
